'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { Button, cn, Skeleton } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { Deposit } from '@shared/icons/system';
import { NumberFormats, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { MyAccountSheet } from '../../../MyAccountSheet/MyAccountSheet';
import type { LifetimeNetDepositsResponse } from '../../types';

type LifetimeNetDepositsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentClassName?: string;
  trigger: React.ReactNode;
};

function LifetimeNetDepositsSheet({ open, onOpenChange, contentClassName, trigger }: LifetimeNetDepositsSheetProps) {
  const t = useTranslations('my_account.wallet.lifetime_net_deposits_sheet');
  const { session } = useSession();
  const playerId = session?.profile?.id;
  const formatCurrency = useCurrencyFormatter();
  const { isLifetimeNetDepositsWithDecimals } = getSiteConfig().MY_ACCOUNT_SETTINGS;
  const numberFormat = isLifetimeNetDepositsWithDecimals ? NumberFormats.WITH_DECIMALS : NumberFormats.WITHOUT_DECIMALS;

  const {
    data: lifetimeNetDepositsData,
    isError: isLifetimeNetDepositsError,
    isLoading: isLifetimeNetDepositsLoading,
  } = useApiQuery<LifetimeNetDepositsResponse>(API_ROUTES.MY_ACCOUNT.WALLET.GET_LIFETIME_NET_DEPOSITS.replace(':playerId', playerId || ''), {
    queryOptions: {
      enabled: !!playerId,
    },
    credentials: true,
  });

  const { deposit, withdrawal, totalNetDeposits } = lifetimeNetDepositsData || {};
  const formatWalletAmount = (value?: number) =>
    formatCurrency(value || 0)
      .format(numberFormat)
      .get();

  return (
    <MyAccountSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t('sheet_title')}
      contentClassName={cn(contentClassName, 'max-w-md')}
      trigger={trigger}
      data-testid="lifetime-net-deposits"
    >
      <div className="flex flex-col p-6 pt-8 gap-8" data-track-feature="My Account" data-track-container="Lifetime Limits">
        <div className="flex flex-col gap-5 items-start justify-start w-full">
          <Deposit size="extra-extra-large" data-testid="deposit-icon" />
          <h4>{t('title')}</h4>
        </div>

        <div className="flex flex-col gap-2.5 p-5 bg-container rounded-md">
          <div className="flex justify-between">
            <span className="paragraph-strong">{t('deposits')}</span>
            {/**
             * NOTE: Rather than displaying a zero value when an error occurs, show the loading state instead as this is likely to be less confusing for users.
             * TODO: Implement a dedicated error state when a design is provided.
             */}
            {isLifetimeNetDepositsError || isLifetimeNetDepositsLoading ? (
              <Skeleton className="h-6 w-12" data-testid="deposits-skeleton" />
            ) : (
              <span className="paragraph-strong" data-testid="lifetime-net-deposits-deposit-value">
                {formatWalletAmount(deposit)}
              </span>
            )}
          </div>
          <div className="flex justify-between">
            <span className="paragraph-strong">{t('withdrawal')}</span>
            {/**
             * NOTE: Rather than displaying a zero value when an error occurs, show the loading state instead as this is likely to be less confusing for users.
             * TODO: Implement a dedicated error state when a design is provided.
             */}
            {isLifetimeNetDepositsError || isLifetimeNetDepositsLoading ? (
              <Skeleton className="h-6 w-12" data-testid="withdrawal-skeleton" />
            ) : (
              <span className="paragraph-strong" data-testid="lifetime-net-deposits-withdrawal-value">
                {formatWalletAmount(withdrawal)}
              </span>
            )}
          </div>
          <div className="flex justify-between">
            <span className="paragraph-strong">{t('total_net_deposits')}</span>
            {/**
             * NOTE: Rather than displaying a zero value when an error occurs, show the loading state instead as this is likely to be less confusing for users.
             * TODO: Implement a dedicated error state when a design is provided.
             */}
            {isLifetimeNetDepositsError || isLifetimeNetDepositsLoading ? (
              <Skeleton className="h-6 w-12" data-testid="total-net-deposits-skeleton" />
            ) : (
              <span className="paragraph-strong" data-testid="lifetime-net-deposits-total-value">
                {formatWalletAmount(totalNetDeposits)}
              </span>
            )}
          </div>
        </div>

        <div data-track-container="Actions">
          <Button onClick={() => onOpenChange(false)} className="w-full" data-track-name="Done" data-testid="lifetime-net-deposits-close-button">
            {t('close_button')}
          </Button>
        </div>
      </div>
    </MyAccountSheet>
  );
}

export { LifetimeNetDepositsSheet };
